/**
 * GLI STATI DI UNA GIOCATA E LE LORO PAROLE, in un posto solo.
 *
 * Stavano dentro la lista scommesse (MyBets.js). Li legge anche la striscia
 * delle ultime giocate sotto la schedina, e la regola e' «stessa cosa, stesso
 * nome»: la stessa giocata non puo' chiamarsi «Rifiutata» in una pagina e in
 * un altro modo nella colonna accanto. Quindi stanno qui, e le due pagine li
 * importano.
 *
 * LE PAROLE si leggono nella lingua di chi guarda NEL MOMENTO in cui si
 * leggono (i18next.t dentro un getter): l'elenco e' scritto una volta, la
 * lingua puo' cambiare dopo.
 */
import i18next from 'i18next';
import { getCurrentUserId } from './currentUser';

export const typeLabels = {
    get SINGLE() { return i18next.t('Singola'); },
    get MULTIPLE() { return i18next.t('Multipla'); },
    get SYSTEM() { return i18next.t('Sistema'); },
    get INTEGRAL() { return i18next.t('Integrale'); },
};

/* GLI STATI, in ordine di precedenza.
   Una giocata puo' soddisfare piu' condizioni insieme: liquidata, vinta e
   pagata sono vere tutte e tre sullo stesso ticket. Chi guarda vuole UNA
   parola e UN colore, quindi serve dire quale vince — e dirlo in un posto
   solo, non tre volte in tre punti del file.

   L'ordine e' quello della vita della giocata letta a ritroso: annullata
   cancella tutto il resto; pagata chiude la storia; da incassare e' la cosa
   che chiede un'azione; poi com'e' andata; in corso e' quello che resta.

   I COLORI non sono sei tinte a caso. Vinta, da incassare e pagata non sono
   tre stati scollegati: sono tre momenti della stessa cosa, e stanno nello
   stesso verde a tre luminosita'. L'occhio le legge come una sequenza senza
   che nessuno gliela spieghi.

   Dentro quella sequenza, il piu' acceso e' DA RISCUOTERE: e' l'unico che
   chiede di fare qualcosa, perche' c'e' del denaro fermo. Si chiama cosi' e
   non "da incassare" perche' la colonna della tabella dice gia' Riscossa: due
   parole per la stessa cosa fanno pensare che siano due cose.

   Dal backoffice la stessa riga andra' letta "Da pagare": cambia il lato, non
   lo stato — e' il servizio a dire AWAITING_PAYMENT in tutti e due i casi. Pagata e' il verde
   piu' spento, che la storia e' chiusa. Fuori dalla sequenza restano il rosso
   di una perdita, l'ambra di quello che deve ancora succedere e il grigio di
   chi e' uscito dal gioco.

   Il cyan non compare: e' il colore dell'interfaccia — il bottone Cerca, i
   collegamenti, l'accento della skin — e un quadratino di quel colore
   sembrerebbe qualcosa su cui si clicca. */
export const STATI = [
    { chiave: 'VOIDED',           get etichetta() { return i18next.t('Annullata'); },    colore: '#5b646d',
      quando: (b) => b.status === 'VOIDED' && !annullataDaMe(b) },
    /* ANNULLATA DA LUI, dal ticket aperto (BloccoAnnulla). E' lo stesso
       stato del servizio, VOIDED, e porta la stessa chiave e lo stesso
       grigio: il filtro «Annullata» le prende tutte e due e la cifra resta
       «Rimborsato». Cambia solo la parola, e solo per chi l'ha annullata:
       un'agenzia che guarda la giocata di un suo giocatore legge
       «Annullata», non «Annullata da te». Sta DOPO la riga generica perche'
       il filtro prende la prima riga con quella chiave. */
    { chiave: 'VOIDED',           get etichetta() { return i18next.t('Annullata da te'); }, colore: '#5b646d',
      quando: (b) => b.status === 'VOIDED' && annullataDaMe(b) },
    /* VENDUTA COL CASH OUT: chiusa, e ne' vinta ne' persa -- l'ha ricomprata
       il banco. Un blu suo, fuori dalla sequenza dei verdi (non e' una
       vincita) e lontano dal cyan dell'interfaccia. Prima di PAID e di WON:
       il servizio la chiude con esito CASHOUT e niente altro deve vincere. */
    { chiave: 'VENDUTA',          get etichetta() { return i18next.t('Venduta'); },      colore: '#4c8dd6',
      quando: (b) => b.status === 'VENDUTA' },
    { chiave: 'IN_CASHOUT',       get etichetta() { return i18next.t('Cash out in corso'); }, colore: '#7fb0e6',
      quando: (b) => b.status === 'IN_CASHOUT' },
    /* L'ANNULLAMENTO IN CORSO: lui ha annullato, il servizio sta
       restituendo la posta e per un attimo la giocata non e' ne' in gioco
       ne' annullata. Lo stesso grigio chiaro di «Rimborso in corso»: e' la
       stessa cosa vista da lui, soldi che tornano. */
    { chiave: 'IN_ANNULLO',       get etichetta() { return i18next.t('Annullamento in corso'); }, colore: '#9aa3ab',
      quando: (b) => b.status === 'IN_ANNULLO' },
    { chiave: 'PAID',             get etichetta() { return i18next.t('Pagata'); },       colore: '#2f7d4f',
      quando: (b) => b.status === 'PAID' },
    { chiave: 'AWAITING_PAYMENT', get etichetta() { return i18next.t('Da riscuotere'); }, colore: '#5fd98a',
      quando: (b) => b.status === 'AWAITING_PAYMENT' },
    { chiave: 'WON',              get etichetta() { return i18next.t('Vinta'); },        colore: '#4d9f2f',
      quando: (b) => b.outcome === 'WON' },
    { chiave: 'LOST',             get etichetta() { return i18next.t('Persa'); },        colore: '#e2564e',
      quando: (b) => b.outcome === 'LOST' },
    /* PRIMA DI PENDING, che e' il ripiego di tutti: senza questa riga una
       giocata in riserva cadeva su "In corso", cioe' su "accettata, aspetta
       l'esito" — ed e' il contrario. La posta e' stata presa ma nessuno l'ha
       ancora accettata, e l'unico che deve fare qualcosa siamo noi. */
    /* VIOLA E NON ARANCIONE. Era #f5a524 accanto all'arancione di "In corso"
       (#e98f00): due toni a un passo l'uno dall'altro, e in un elenco di
       quadratini piccoli non si distinguevano — proprio i due stati che
       bisogna distinguere, perche' uno vuol dire "e' partita" e l'altro "non
       ancora". Il viola non e' usato da nessun altro stato e non e' l'accento
       della skin, che resta riservato alle cose su cui si clicca. */
    /* L'UNICA RIGA CHE ASPETTA LUI. Il banco ha accettato a condizioni
       diverse e la giocata e' ferma finche' non risponde: e' l'unico stato
       con un tasto e un tempo che scorre, e il colore e' uno che nessun
       altro stato usa -- giallo, piu' chiaro dell'arancione di «In corso»
       e lontano dal viola di «In accettazione», perche' nei quadratini
       piccoli si distinguono per luminosita', non per tonalita'. */
    { chiave: 'RIPROPOSTA',       get etichetta() { return i18next.t('Ti hanno riproposto'); }, colore: '#ffd43b',
      quando: (b) => b.status === 'RIPROPOSTA' },
    { chiave: 'DA_ACCETTARE',     get etichetta() { return i18next.t('In accettazione'); }, colore: '#a07cff',
      quando: (b) => b.status === 'DA_ACCETTARE' },

    /* LE RISERVE FINITE, che prima non arrivavano proprio: il servizio le
       toglieva dall'elenco. Adesso ci sono, e devono avere parole loro —
       cadere su "In corso" direbbe a un giocatore che ha una scommessa viva
       dove invece ha i soldi tornati sul conto.

       RIFIUTATA e SCADUTA restano due parole diverse anche per lui. Per il
       cliente l'effetto e' lo stesso — la giocata non e' partita — ma
       "scaduta" spiega una cosa che "rifiutata" non dice: che non l'ha
       rifiutata nessuno, e' finito il tempo. E' una risposta piu' onesta da
       dare, non meno. */
    { chiave: 'RIFIUTATA',        get etichetta() { return i18next.t('Rifiutata'); },    colore: '#c2566f',
      quando: (b) => b.status === 'RIFIUTATA' },
    { chiave: 'SCADUTA',          get etichetta() { return i18next.t('Scaduta'); },      colore: '#7a838c',
      quando: (b) => b.status === 'SCADUTA' },
    { chiave: 'IN_RIFIUTO',       get etichetta() { return i18next.t('Rimborso in corso'); }, colore: '#9aa3ab',
      quando: (b) => b.status === 'IN_RIFIUTO' },

    { chiave: 'PENDING',          get etichetta() { return i18next.t('In corso'); },     colore: '#e98f00',
      quando: () => true },
];

/* LA RICEVUTA «ACCETTATA» NON STA NELL'ELENCO QUI SOPRA, di proposito.
   Ci e' stata per un giro: PENDING con accettataIl da poco usciva come
   «Accettata» in verde, e nella lista scommesse la colonna Esito e' una
   colonna di quadratini dove il verde vuol dire UNA cosa sola -- vinta. Un
   giocatore che aveva appena detto si' a una riproposta si e' visto il
   quadratino verde e l'ha letto come una vincita. Quindi per la lista una
   giocata accettata e' «In corso», arancione, e basta: e' partita, l'esito
   deve ancora arrivare.

   La ricevuta serve solo nella striscia sotto la schedina, dove la parola
   «Accettata» sta scritta per intero accanto al pallino e dove chi ha visto
   la riga viola «In accettazione» o quella gialla della riproposta deve
   vedere com'e' finita. Dura dieci minuti da accettataIl, poi la riga
   torna «In corso» come tutte. E' PENDING letto con l'ora, non uno stato del
   servizio: nel filtro di «tutte» non compare. */
export const RICEVUTA_ACCETTATA = Object.freeze({
    chiave: 'ACCETTATA', get etichetta() { return i18next.t('Accettata'); }, colore: '#4d9f2f',
});

export const accettataDaPoco = (b) => b.status === 'PENDING' && !!b.accettataIl
    && (Date.now() - new Date(b.accettataIl).getTime()) < 10 * 60 * 1000;

/* Annullata dal cliente, e il cliente e' chi guarda. Solo il titolare del
   conto puo' annullare dal sito (il servizio rifiuta tutti gli altri),
   quindi «da te» e' vero esattamente quando la giocata e' sua. */
function annullataDaMe(b) {
    if (!b || !(b.annullataDalCliente || (b.raw && b.raw.annullataDalCliente))) return false;
    const io = getCurrentUserId();
    return !!io && b.userId === io;
}

/* Il primo che risponde di si'. Tutto il resto della pagina passa di qui:
   la pastiglia sulla scheda, il quadratino in tabella, la striscia laterale. */
export const statoDi = (b) => STATI.find((s) => s.quando(b)) || STATI[STATI.length - 1];

export const etichettaStato = (b) => statoDi(b).etichetta;
export const coloreStato = (b) => statoDi(b).colore;
